/**
 * Purpose: Host-only panel for docking points from a player's class grade for bad behavior.
 * Owns: Player selection, dock percentage selection, confirmation flow, and toast feedback.
 * Notes: Only accessible during an active class (not during setup or results).
 *        Designed as a slide-up panel triggered from the period header.
 *        Uses a two-step flow (select → confirm) to prevent accidental docks.
 *        The dock percentage is additive — multiple docks stack on the same class.
 *        All colors reference theme tokens (CS-22). Config-driven options (CS-21).
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Player } from '../../types/game';
import { BEHAVIOR_DOCK_OPTIONS } from '../../config/classConfig';

/** Duration (ms) the confirmation toast stays visible before auto-dismissing. */
const TOAST_DISPLAY_MS = 2500;

interface BehaviorDockProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called to close the panel */
  onClose: () => void;
  /** All players in the current game */
  players: Player[];
  /** Pre-selected player ID (active player by default, host can change) */
  activePlayerId: string;
  /** Called when the host confirms a dock — applies it to the game state */
  onApplyDock: (playerId: string, dockPercent: number) => void;
}

export function BehaviorDock({
  isOpen,
  onClose,
  players,
  activePlayerId,
  onApplyDock,
}: BehaviorDockProps) {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>(activePlayerId);
  const [selectedDock, setSelectedDock] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Reset selections when the panel opens or the active player changes */
  useEffect(() => {
    if (isOpen) {
      setSelectedPlayerId(activePlayerId);
      setSelectedDock(null);
      setShowConfirm(false);
    }
  }, [isOpen, activePlayerId]);

  /* Clear toast timer on unmount */
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const handleDockSelect = useCallback((dockValue: number) => {
    setSelectedDock(dockValue);
    setShowConfirm(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedDock === null) return;

    const player = players.find((p) => p.id === selectedPlayerId);
    const playerName = player?.name ?? 'Player';

    onApplyDock(selectedPlayerId, selectedDock);

    /* Show feedback toast */
    setToastMessage(`\u2212${selectedDock}% applied to ${playerName}`);

    /* Clear any existing toast timer */
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
    }, TOAST_DISPLAY_MS);

    /* Reset and close */
    setSelectedDock(null);
    setShowConfirm(false);
    onClose();
  }, [selectedPlayerId, selectedDock, players, onApplyDock, onClose]);

  const handleCancel = useCallback(() => {
    setSelectedDock(null);
    setShowConfirm(false);
  }, []);

  const selectedPlayer = players.find((p) => p.id === selectedPlayerId);
  const isSoloMode = players.length === 1;

  if (!isOpen && !toastMessage) return null;

  return (
    <>
      {/* Toast feedback — shown even after panel closes */}
      {toastMessage && (
        <div style={styles.toast} role="status" aria-live="polite">
          <span style={styles.toastText}>{toastMessage}</span>
        </div>
      )}

      {/* Overlay + panel */}
      {isOpen && (
        <div style={styles.overlay} onClick={onClose} role="presentation">
          <div
            style={styles.panel}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Behavior dock controls"
          >
            {/* Header */}
            <div style={styles.header}>
              <span style={styles.headerTitle}>Behavior Dock</span>
              <button
                style={styles.closeButton}
                onClick={onClose}
                aria-label="Close behavior dock"
              >
                {'\u2715'}
              </button>
            </div>

            {/* Player selector — hidden in solo mode */}
            {!isSoloMode && (
              <div style={styles.section}>
                <span style={styles.sectionLabel}>Player</span>
                <div style={styles.playerList}>
                  {players.map((player) => (
                    <button
                      key={player.id}
                      style={{
                        ...styles.playerButton,
                        ...(player.id === selectedPlayerId
                          ? styles.playerButtonActive
                          : {}),
                        borderColor: player.color,
                      }}
                      onClick={() => setSelectedPlayerId(player.id)}
                      aria-label={`Select ${player.name}`}
                      aria-pressed={player.id === selectedPlayerId}
                    >
                      <span
                        style={{
                          ...styles.playerDot,
                          backgroundColor: player.color,
                        }}
                      />
                      <span style={styles.playerName}>{player.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dock percentage options */}
            {!showConfirm && (
              <div style={styles.section}>
                <span style={styles.sectionLabel}>
                  Dock {selectedPlayer?.name ?? 'player'}
                </span>
                <div style={styles.dockOptions}>
                  {BEHAVIOR_DOCK_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      style={styles.dockButton}
                      onClick={() => handleDockSelect(option.value)}
                      aria-label={`Dock ${option.value} percent from grade`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation step */}
            {showConfirm && selectedDock !== null && (
              <div style={styles.section}>
                <p style={styles.confirmText}>
                  Dock {'\u2212'}{selectedDock}% from{' '}
                  <strong>{selectedPlayer?.name ?? 'player'}</strong>'s current
                  class grade?
                </p>
                <div style={styles.confirmActions}>
                  <button
                    style={styles.cancelButton}
                    onClick={handleCancel}
                    aria-label="Cancel dock"
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.confirmButton}
                    onClick={handleConfirm}
                    aria-label="Confirm dock"
                  >
                    Confirm Dock
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* _______________________________________________
 *              STYLES (CS-22: theme tokens only)
 * _______________________________________________ */

const styles: Record<string, React.CSSProperties> = {
  /* Full-screen overlay behind the panel */
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 1000,
  },
  /* Slide-up panel */
  panel: {
    width: '100%',
    maxWidth: '440px',
    backgroundColor: 'var(--bg-surface)',
    borderRadius: '16px 16px 0 0',
    padding: '1rem 1.25rem 1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  /* Panel header with title and close button */
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: 'var(--incorrect)',
    fontFamily: 'var(--font-family-display)',
    letterSpacing: '0.03em',
  },
  closeButton: {
    fontSize: '1.1rem',
    color: 'var(--muted)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    minHeight: '36px',
    minWidth: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Section wrapper */
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  sectionLabel: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  /* Player selector row */
  playerList: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  playerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.75rem',
    borderRadius: '8px',
    border: '2px solid transparent',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    cursor: 'pointer',
    minHeight: '40px',
    minWidth: '40px',
  },
  playerButtonActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    borderWidth: '2px',
    borderStyle: 'solid',
  },
  playerDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  playerName: {
    fontSize: '0.85rem',
    color: 'var(--text-light)',
    fontWeight: '500',
  },
  /* Dock percentage buttons */
  dockOptions: {
    display: 'flex',
    gap: '0.75rem',
  },
  dockButton: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '10px',
    fontSize: '1.1rem',
    fontWeight: '700',
    backgroundColor: 'var(--incorrect)',
    color: 'var(--text-light)',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center' as const,
    fontFamily: 'var(--font-family)',
  },
  /* Confirmation step */
  confirmText: {
    fontSize: '0.95rem',
    color: 'var(--text-light)',
    textAlign: 'center' as const,
    lineHeight: 1.4,
    fontFamily: 'var(--font-family-handwriting)',
  },
  confirmActions: {
    display: 'flex',
    gap: '0.75rem',
  },
  cancelButton: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '600',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'var(--text-light)',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
  },
  confirmButton: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '700',
    backgroundColor: 'var(--incorrect)',
    color: 'var(--text-light)',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
  },
  /* Toast notification — floats at the top of the screen */
  toast: {
    position: 'fixed',
    top: '1rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--incorrect)',
    color: 'var(--text-light)',
    padding: '0.6rem 1.25rem',
    borderRadius: '8px',
    zIndex: 1100,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
  toastText: {
    fontSize: '0.95rem',
    fontWeight: '600',
    fontFamily: 'var(--font-family)',
  },
};
